import { LayoutTemplate } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils.js';
import type { Exam } from '../../../../../db/exam.js';
import { useStructureState } from './hooks/use-structure-state.js';
import { StructureTopBar, StructurePageRenderer } from './components/structure-panels.js';
import type { StructureEditorTask, StructureEditorAdditionalInfo } from './utils.js';

const DEFAULT_LEFT_PANEL_WIDTH = 55;
const RESIZE_STORAGE_KEY = 'gradias-structure-left-panel-width';
// The card column must stay within 25-70% of the container AND never render narrower than this, even if
// that means overriding the 25% floor (e.g. on a narrow canvas where 25% would be under 200px).
const MIN_CARD_COLUMN_PX = 200;

/** The largest leftPanelWidth (%) that still leaves the card column at least MIN_CARD_COLUMN_PX wide. */
function maxLeftPanelWidthForContainer(containerWidthPx: number): number {
  if (containerWidthPx <= 0) return 75;
  return Math.min(75, 100 - (MIN_CARD_COLUMN_PX / containerWidthPx) * 100);
}

export interface StructureEditorProps {
  exam: Exam | null;
  pages: string[];
  isReadOnly?: boolean;
  onSave: (tasks: StructureEditorTask[], options?: { rerunDescriptionOCRBoxIds?: string[] }) => Promise<void>;
  onSaveAdditionalInfo?: (additionalInfo: StructureEditorAdditionalInfo[]) => Promise<void>;
}

export function StructureEditor(props: StructureEditorProps) {
  const { t } = useTranslation();
  const store = useStructureState(props);

  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LEFT_PANEL_WIDTH;
    const stored = localStorage.getItem(RESIZE_STORAGE_KEY);
    return stored ? Math.max(30, Math.min(75, parseInt(stored, 10))) : DEFAULT_LEFT_PANEL_WIDTH;
  });

  const [isResizing, setIsResizing] = useState(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  const handleResizeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current) return;

    setIsResizing(true);
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = leftPanelWidth;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    if (!isResizing) return;

    const handlePointerMove = (e: PointerEvent) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      rafRef.current = requestAnimationFrame(() => {
        const deltaX = e.clientX - resizeStartXRef.current;
        const containerWidthPx = containerRef.current!.offsetWidth;
        const deltaPercent = (deltaX / containerWidthPx) * 100;
        const maxLeftPanelWidth = maxLeftPanelWidthForContainer(containerWidthPx);
        const newWidth = Math.max(30, Math.min(maxLeftPanelWidth, resizeStartWidthRef.current + deltaPercent));

        setLeftPanelWidth(newWidth);
      });
    };

    const handlePointerUp = () => {
      setIsResizing(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      setLeftPanelWidth(currentWidth => {
        localStorage.setItem(RESIZE_STORAGE_KEY, Math.round(currentWidth).toString());
        return currentWidth;
      });

      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isResizing]);

  // Re-clamp against the card column's 200px floor whenever the container itself is resized (e.g. the
  // outer canvas panel getting narrower), not just while dragging the divider.
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      const containerWidthPx = entries[0]?.contentRect.width;
      if (!containerWidthPx) return;
      const maxLeftPanelWidth = maxLeftPanelWidthForContainer(containerWidthPx);
      setLeftPanelWidth(current => Math.min(current, maxLeftPanelWidth));
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  if (!props.pages || props.pages.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
          <LayoutTemplate className="h-7 w-7 text-slate-400 dark:text-slate-500" />
        </div>
        <p className="font-medium text-slate-500 dark:text-slate-400">{t('Canvas.structureEditor.noPages')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden">
      <StructureTopBar store={store} />

      <div
        ref={containerRef}
        className={cn(
          'relative min-h-0 flex-1 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 shadow-inner overflow-hidden',
          isResizing && 'select-none',
        )}
      >
        <div className={cn('w-full h-full', isResizing && 'pointer-events-none')}>
          <StructurePageRenderer store={store} leftPanelWidth={leftPanelWidth} />
        </div>

        <div
          onPointerDown={handleResizeStart}
          className={cn(
            'absolute top-0 bottom-0 z-50 group flex items-center justify-center cursor-col-resize transition-colors duration-150',
            'w-1 hover:w-1 -ml-[2px] rounded-full',
            isResizing ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600 hover:bg-blue-400',
          )}
          style={{ left: `${leftPanelWidth}%` }}
        >
          <div
            className={cn(
              'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1 p-1 rounded-full cursor-col-resize transition-opacity duration-150',
              isResizing
                ? 'opacity-100 bg-blue-600'
                : 'opacity-0 group-hover:opacity-100 bg-white dark:bg-slate-700 shadow-md border border-slate-200 dark:border-slate-600',
            )}
          >
            {[...Array(3)].map((_, i) => (
              <div key={i} className={cn('w-1 h-1 rounded-full', isResizing ? 'bg-white' : 'bg-slate-400 dark:bg-slate-500')} />
            ))}
          </div>
          <div className="absolute inset-y-0 -left-3 -right-3 cursor-col-resize z-10" />
        </div>
      </div>
    </div>
  );
}

export { serializeStructureEditorTasks } from './utils.js';
